import React, { useState } from 'react'
import { 
  BarChart3, 
  Database, 
  Users, 
  BookOpen, 
  FileText, 
  Activity, 
  FolderOpen,
  Image,
  Music,
  Crown,
  ChevronRight
} from 'lucide-react'

interface SidebarProps {
  currentView: 'dashboard' | 'data-browser' | 'file-browser' | 'role-management'
  selectedTable?: 'users' | 'books' | 'chapters' | 'api_usage'
  selectedFilePath?: string
  onNavigationChange: (view: 'dashboard' | 'data-browser' | 'file-browser' | 'role-management', table?: 'users' | 'books' | 'chapters' | 'api_usage', filePath?: string) => void
}

const Sidebar: React.FC<SidebarProps> = ({
  currentView,
  selectedTable,
  selectedFilePath,
  onNavigationChange
}) => {
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['data-browser']))

  const toggleSection = (sectionId: string) => {
    const newExpanded = new Set(expandedSections)
    if (newExpanded.has(sectionId)) {
      newExpanded.delete(sectionId)
    } else {
      newExpanded.add(sectionId)
    }
    setExpandedSections(newExpanded)
  }

  const handleDataTableClick = (table: 'users' | 'books' | 'chapters' | 'api_usage') => {
    onNavigationChange('data-browser', table)
  }

  const handleFilePathClick = (filePath: string) => {
    onNavigationChange('file-browser', undefined, filePath)
  }

  const dataTables = [
    { id: 'users', name: 'Users', icon: Users },
    { id: 'books', name: 'Books', icon: BookOpen },
    { id: 'chapters', name: 'Chapters', icon: FileText },
    { id: 'api_usage', name: 'API Usage', icon: Activity }
  ] as const

  const filePaths = [
    { path: 'images/covers/', name: 'Cover Images', icon: Image },
    { path: 'audio/', name: 'Audio Files', icon: Music }
  ]

  return (
    <aside className="sidebar">
      {/* Dashboard Section */}
      <div className="sidebar-section">
        <div 
          className={`section-header ${currentView === 'dashboard' ? 'active' : ''}`}
          onClick={() => onNavigationChange('dashboard')}
        >
          <div className="section-title">
            <span className="section-icon"><BarChart3 size={16} /></span>
            Dashboard
          </div>
        </div>
      </div>

      {/* Data Browser Section */}
      <div className="sidebar-section">
        <div 
          className={`section-header ${currentView === 'data-browser' ? 'active' : ''}`}
          onClick={() => toggleSection('data-browser')}
        >
          <div className="section-title">
            <span className="section-icon"><Database size={16} /></span>
            Data Browser
          </div>
          <span className={`expand-icon ${expandedSections.has('data-browser') ? 'expanded' : ''}`}>
            <ChevronRight size={14} />
          </span>
        </div>
        <div className={`section-children ${expandedSections.has('data-browser') ? 'expanded' : 'collapsed'}`}>
          {dataTables.map((table) => (
            <div
              key={table.id}
              className={`child-item ${selectedTable === table.id ? 'active' : ''}`}
              onClick={() => handleDataTableClick(table.id)}
            >
              <span className="child-item-icon"><table.icon size={14} /></span>
              {table.name}
            </div>
          ))}
        </div>
      </div>

      {/* File Browser Section */}
      <div className="sidebar-section">
        <div 
          className={`section-header ${currentView === 'file-browser' ? 'active' : ''}`}
          onClick={() => toggleSection('file-browser')}
        >
          <div className="section-title">
            <span className="section-icon"><FolderOpen size={16} /></span>
            File Browser
          </div>
          <span className={`expand-icon ${expandedSections.has('file-browser') ? 'expanded' : ''}`}>
            <ChevronRight size={14} />
          </span>
        </div>
        <div className={`section-children ${expandedSections.has('file-browser') ? 'expanded' : 'collapsed'}`}>
          {filePaths.map((file) => (
            <div
              key={file.path}
              className={`child-item ${selectedFilePath === file.path ? 'active' : ''}`}
              onClick={() => handleFilePathClick(file.path)}
            >
              <span className="child-item-icon"><file.icon size={14} /></span>
              {file.name}
            </div>
          ))}
        </div>
      </div>

      {/* Role Management Section */}
      <div className="sidebar-section">
        <div 
          className={`section-header ${currentView === 'role-management' ? 'active' : ''}`}
          onClick={() => onNavigationChange('role-management')}
        >
          <div className="section-title">
            <span className="section-icon"><Crown size={16} /></span>
            Role Management
          </div>
        </div>
      </div>
    </aside>
  )
}

export default Sidebar